import './style.scss';

import cn from 'classnames';
import React, { HTMLAttributes, useCallback } from 'react';

import { useTypedDispatch } from 'store';
import { setCopyPopupShow } from 'store/slices/ui';
import { Tooltip } from 'ui';
import { addressShortView, addressView } from 'utils';
import { calculateScanLink } from 'utils/calculates';
import { copyToClipboard } from 'utils/clipboard';
import { ENetwork } from 'web3';

import { LinkSmall } from 'assets/icons';

interface ITableAddressChipProps extends HTMLAttributes<HTMLDivElement> {
  address: string;
  withScanLink?: boolean;
  network?: ENetwork;
  addressType?: 'address' | 'transaction' | 'token';
  format?: 'short' | 'medium' | 'full';
  className?: string;
  isImported?: boolean;
  origin?: 'imported' | 'generated';
}

const TableAddressChip: React.FC<ITableAddressChipProps> = ({
  address,
  withScanLink = true,
  network,
  addressType,
  format = 'medium',
  className,
  isImported = false,
  origin,
  ...rest
}) => {
  const dispatch = useTypedDispatch();
  const resolvedOrigin = origin ?? (isImported ? 'imported' : undefined);

  const handleCopyAddress = useCallback(async () => {
    try {
      await copyToClipboard(address);
      dispatch(setCopyPopupShow(true));
    } catch (error) {
      console.log('error: ', error);
    }
  }, [address, dispatch]);

  const indicatorClass = resolvedOrigin
    ? cn('mm-table-address-chip__badge', {
        'mm-table-address-chip__badge--imported mm-table-address-chip__imported':
          resolvedOrigin === 'imported',
        'mm-table-address-chip__badge--generated': resolvedOrigin === 'generated',
      })
    : undefined;

  return (
    <div {...rest} className={cn({ [className ?? '']: !!className }, 'mm-table-address-chip')}>
      <span className="mm-table-address-chip__address" onClick={handleCopyAddress}>
        {format === 'medium'
          ? addressView(address)
          : format === 'short'
            ? addressShortView(address)
            : address}
      </span>

      {withScanLink && (
        <a
          className="mm-table-address-chip__scan-link"
          href={calculateScanLink({
            address,
            network: network ?? ENetwork.bsc,
            addressType: addressType ?? 'address',
          })}
          target="_blank"
          rel="noreferrer"
        >
          <LinkSmall />
        </a>
      )}

      {resolvedOrigin && indicatorClass && (
        <Tooltip
          content={resolvedOrigin === 'generated' ? 'Generated' : 'Imported'}
          trigger={<span className={indicatorClass} />}
          options={{ offset: 10, placement: 'top' }}
        />
      )}
    </div>
  );
};

export { TableAddressChip };
